import { Alert, Pressable, ScrollView, Text, View } from 'react-native';
import type { Reg } from '../store/types';
import type { Colecao, Ctx, ModDef, PainelProps } from './tipos';
import { dataBr, diasAte, junta, num, op } from './util';
import { novoReg, salvarReg } from '../store/modulos';
import { useDoc } from '../store/store';
import { moedaDe, precoDe } from '../store/financas';
import { dinheiro } from '../lib/moeda';
import { C, alpha } from '../components/theme';
import { norm, uid } from '../lib/date';
import { N_, t } from '../lib/i18n';

const DIAS = [N_('Segunda'), N_('Terça'), N_('Quarta'), N_('Quinta'), N_('Sexta'), N_('Sábado'), N_('Domingo')];
const REFEICOES = [N_('Café da manhã'), N_('Almoço'), N_('Lanche'), N_('Jantar')];
const UNIDADES = /^(x[ií]caras?|colher(es)?( de (sopa|ch[aá]|sobremesa))?|copos?|latas?|pacotes?|caixas?|potes?|dentes?|ma[cç]os?|fatias?|pitadas?|unidades?|un|kg|g|gr|mg|ml|l|litros?|cups?|tbsp|tsp|oz|lb|cloves?)\b\.?\s*(de\s+|of\s+)?/i;

/** "2 xícaras de farinha de trigo" → "farinha de trigo" (para comparar com a despensa). */
export function nomeIngrediente(linha: string) {
  let s = linha.replace(/^[\s•\-*]+/, '').replace(/^[\d.,/½¼¾⅓⅔\s]+/, '').trim();
  s = s.replace(UNIDADES, '').replace(/^(de|of)\s+/i, '').replace(/\(.*?\)/g, '').replace(/,.*$/, '').trim();
  return s;
}
const ingredientes = (r: Reg) => String(r.c.ingredientes || '').split(/\n|;/).map(x => x.trim()).filter(Boolean);
const temNaDespensa = (nome: string, despensa: Reg[]) => {
  const n = norm(nome);
  return despensa.some(d => { const x = norm(String(d.c.item || '')); return !!x && num(d.c.qtd) > 0 && (n.includes(x) || x.includes(n)) && !(d.c.validade && diasAte(d.c.validade) < 0); });
};

/** Lista de compras: ingredientes das receitas do cardápio que não estão na despensa, mais o que acabou. */
export function listaDaSemana(ctx: Ctx) {
  const despensa = ctx.regs.filter(r => r.col === 'despensa');
  const receitas = ctx.regs.filter(r => r.col === 'cardapio').map(r => ctx.porId(r.c.receita)).filter(Boolean) as Reg[];
  const faltam = new Map<string, string>();
  for (const rc of receitas) for (const l of ingredientes(rc)) {
    const nome = nomeIngrediente(l);
    if (nome && !temNaDespensa(nome, despensa) && !faltam.has(norm(nome))) faltam.set(norm(nome), l);
  }
  for (const d of despensa) if (num(d.c.qtd) <= 0 && d.c.item && !faltam.has(norm(d.c.item))) faltam.set(norm(d.c.item), d.c.item);
  return [...faltam.values()];
}

function Semana({ ctx, lista, abrir, novo }: PainelProps) {
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={{ gap: 8, paddingBottom: 6 }}>
      {DIAS.map(d => (
        <View key={d} style={{ width: 170, backgroundColor: C.card, borderRadius: 14, padding: 10, gap: 6 }}>
          <Text style={{ color: C.text, fontWeight: '800' }}>{t(d)}</Text>
          {REFEICOES.map(rf => {
            const it = lista.filter(r => r.c.dia === d && r.c.refeicao === rf);
            return (
              <View key={rf} style={{ gap: 3 }}>
                <Pressable onPress={() => novo({ dia: d, refeicao: rf })} hitSlop={4}><Text style={{ color: C.mute, fontSize: 11 }}>{t(rf)} ＋</Text></Pressable>
                {it.map(r => (
                  <Pressable key={r.id} onPress={() => abrir(r)} style={{ backgroundColor: alpha(ctx.cor, 0.16), borderRadius: 8, paddingHorizontal: 8, paddingVertical: 5 }}>
                    <Text style={{ color: C.text, fontSize: 12 }} numberOfLines={2}>{ctx.porId(r.c.receita)?.c.nome || r.c.prato || '?'}</Text>
                  </Pressable>
                ))}
              </View>
            );
          })}
        </View>
      ))}
    </ScrollView>
  );
}

function PainelCompras({ ctx, lista }: PainelProps) {
  function gerar() {
    const novos = listaDaSemana(ctx);
    if (!novos.length) return Alert.alert(t('Nada para comprar'), t('As receitas do cardápio já têm tudo na despensa.'));
    const atual = lista.find(r => !r.c.fechada);
    const r = atual ?? novoReg('compras', null, { nome: t('Compras da semana') });
    const ja = new Set((r.itens ?? []).map(i => norm(nomeIngrediente(i.text))));
    const add = novos.filter(x => !ja.has(norm(nomeIngrediente(x))));
    salvarReg(ctx.mod, { ...r, itens: [...(r.itens ?? []), ...add.map(text => ({ id: uid(), text, done: false }))] });
    Alert.alert(t('Lista de compras'), t('{n} item(ns) adicionado(s).', { n: add.length }));
  }
  // 0.27: preço da última compra (cupons lidos em Finanças)
  const fin = useDoc('financas');
  const aberta = lista.find(r => !r.c.fechada);
  const comPreco = (aberta?.itens ?? []).filter(i => !i.done).map(i => ({ i, p: precoDe(fin, nomeIngrediente(i.text)) })).filter(x => x.p);
  const faltam = (aberta?.itens ?? []).filter(i => !i.done).length;
  const qtd = (txt: string) => { const m = /^(\d{1,2})\s*(x|un\b)?\s+(?!(kg|g|l|ml|lt|litros?|gramas?)\b)/i.exec(txt.trim()); return m ? Number(m[1]) : 1; };
  const total = comPreco.reduce((s, x) => s + (x.p?.preco ?? 0) * qtd(x.i.text), 0);
  return (<>
    <Pressable onPress={gerar} style={{ backgroundColor: ctx.cor, borderRadius: 12, padding: 12, alignItems: 'center' }}>
      <Text style={{ color: '#fff', fontWeight: '800' }}>🛒 {t('Gerar pelo cardápio e pela despensa')}</Text>
    </Pressable>
    {comPreco.length > 0 && (
      <View style={{ backgroundColor: C.card, borderRadius: 12, padding: 12, gap: 4 }}>
        <Text style={{ color: C.text, fontWeight: '700' }}>💰 {t('Pela última compra: cerca de {v}', { v: dinheiro(total, moedaDe(fin)) })}</Text>
        <Text style={{ color: C.mute, fontSize: 11 }}>{t('{n} de {t} item(ns) com preço, dos cupons lidos em Finanças.', { n: comPreco.length, t: faltam })}</Text>
        {comPreco.slice(0, 12).map(({ i, p }) => (
          <View key={i.id} style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text style={{ color: C.sub, fontSize: 12, flex: 1 }} numberOfLines={1}>{i.text}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{dinheiro(p!.preco, moedaDe(fin))}{p!.loja ? ` · ${p!.loja}` : ''}</Text>
          </View>
        ))}
      </View>
    )}
  </>);
}

const cardapio: Colecao = {
  id: 'cardapio', nome: N_('Cardápio'), um: N_('Prato do cardápio'), icone: '🍽️',
  campos: [
    { k: 'dia', r: N_('Dia'), t: 'opcao', op: DIAS },
    { k: 'refeicao', r: N_('Refeição'), t: 'opcao', op: REFEICOES },
    { k: 'receita', r: N_('Receita'), t: 'ref', ref: 'receita' },
    { k: 'prato', r: N_('Ou escreva o prato'), t: 'texto' },
  ],
  titulo: (r, ctx) => ctx.porId(r.c.receita)?.c.nome || r.c.prato || '', sub: r => junta(op(r.c.dia), op(r.c.refeicao)),
  lista: Semana,
};

const receita: Colecao = {
  id: 'receita', nome: N_('Receitas'), um: N_('Receita'), icone: '📖',
  campos: [
    { k: 'nome', r: N_('Nome'), t: 'texto' },
    { k: 'cat', r: N_('Categoria'), t: 'opcao', op: [N_('Café da manhã'), N_('Prato principal'), N_('Acompanhamento'), N_('Salada'), N_('Sopa'), N_('Sobremesa'), N_('Lanche'), N_('Bebida')] },
    { k: 'porcoes', r: N_('Porções'), t: 'numero' },
    { k: 'tempo', r: N_('Tempo de preparo'), t: 'numero', un: 'min' },
    { k: 'ingredientes', r: N_('Ingredientes (um por linha)'), t: 'longo', simples: true, ph: N_('2 xícaras de farinha\n3 ovos\n1 colher de fermento') },
    { k: 'modo', r: N_('Modo de preparo'), t: 'longo' },
    { k: 'nota', r: N_('Avaliação'), t: 'estrelas' },
  ],
  titulo: r => r.c.nome || '', sub: r => junta(op(r.c.cat), num(r.c.tempo) && `${num(r.c.tempo)} min`, num(r.c.porcoes) && t('{n} porções', { n: num(r.c.porcoes) }), num(r.c.nota) && '★'.repeat(num(r.c.nota))),
  ordem: 'titulo', anexos: true,
};

const despensa: Colecao = {
  id: 'despensa', nome: N_('Despensa'), um: N_('Item da despensa'), icone: r => (r.c.local === 'Freezer' ? '🧊' : r.c.local === 'Geladeira' ? '🥶' : '🥫'),
  campos: [
    { k: 'item', r: N_('Item'), t: 'texto', ia: 'nome do produto como está no rótulo, com a marca e o peso ou volume (ex.: Arroz Tio João 5 kg)' },
    { k: 'qtd', r: N_('Quantidade'), t: 'numero', ia: 'número de embalagens; normalmente 1 (não é o peso)' },
    { k: 'un', r: N_('Unidade'), t: 'texto', ph: N_('Ex.: kg, pacotes, latas'), ia: 'tipo de embalagem: pacote, lata, caixa, garrafa, pote...' },
    { k: 'validade', r: N_('Validade'), t: 'data', aviso: [3, 0], ia: 'data de validade impressa (VAL, Validade, Consumir até, Best before); se só tiver mês e ano, use o último dia do mês' },
    { k: 'local', r: N_('Onde fica'), t: 'opcao', op: [N_('Armário'), N_('Geladeira'), N_('Freezer'), N_('Outro')], ia: 'pela conservação indicada no rótulo (ex.: "manter refrigerado" = Geladeira, "congelado" = Freezer)' },
    { k: 'nutricao', r: N_('Informação nutricional'), t: 'longo', ia: 'tabela nutricional do rótulo, um item por linha (porção, calorias, carboidratos, proteínas, gorduras, sódio...), e alergênicos se houver' },
  ],
  titulo: r => r.c.item || '', sub: r => junta(`${num(r.c.qtd)} ${r.c.un || ''}`.trim(), op(r.c.local), num(r.c.qtd) <= 0 && t('acabou')),
  data: 'validade', feito: r => num(r.c.qtd) <= 0, anexos: true,   // 0.65 (B16): foto do rótulo preenche a ficha com a IA
  novo: () => ({ qtd: 1 }),
  filtros: [[N_('Vencendo'), r => !!r.c.validade && diasAte(r.c.validade) <= 7 && diasAte(r.c.validade) >= 0], [N_('Vencidos'), r => !!r.c.validade && diasAte(r.c.validade) < 0],
    [N_('Acabou'), r => num(r.c.qtd) <= 0]],
};

const compras: Colecao = {
  id: 'compras', nome: N_('Compras'), um: N_('Lista de compras'), icone: '🛒',
  campos: [{ k: 'nome', r: N_('Nome da lista'), t: 'texto' }, { k: 'fechada', r: N_('Compra feita'), t: 'sim' }],
  titulo: r => r.c.nome || '', sub: r => (r.c.fechada ? t('feita em {d}', { d: dataBr(r.alt.slice(0, 10)) }) : ''), itens: N_('Itens'), anexos: true,
  ordem: (a, b) => b.criado.localeCompare(a.criado), feito: r => !!r.c.fechada, painel: PainelCompras,
};

export const ALIMENTACAO: ModDef = { id: 'alimentacao', colecoes: [cardapio, receita, despensa, compras] };
